/**
 * API Module - Core API Integration for ArbiWin
 *
 * This module provides centralized API client configuration,
 * request/response handling, and error management for the
 * ArbiWin platform.
 *
 * Following constitution requirements for API integration
 * and modular architecture.
 */

// Import dependencies first
import apiClient from './config/ApiClient';
import { apiHelpers } from '../../config/api';
import { INTERCEPTORS_MODULE_INFO } from './interceptors';
import {
  API_ENDPOINTS,
  URLBuilder,
  ParameterValidator,
  EndpointTester,
  urlBuilder,
  endpointUtils,
  API_VERSIONS
} from './config/ApiEndpoints';
import {
  GlobalErrorHandler,
  RetryHandler,
  globalErrorHandler,
  retryHandler,
  errorUtils,
  handleApiError,
  handleNetworkError,
  handleAuthError,
  handleSubscriptionError,
  ERROR_CATEGORIES,
  ERROR_SEVERITY,
  RECOVERY_ACTIONS
} from './utils/ErrorHandler';
import {
  ResponseProcessor,
  ResponseCache,
  ResponsePerformanceMonitor,
  ResponseValidator,
  ResponseTransformer,
  PaginationHandler,
  MockResponseGenerator,
  responseProcessor,
  responseUtils,
  processApiResponse,
  getCachedResponse,
  validateResponse,
  validateMarketsAnalysis,
  transformMarketsAnalysis,
  generateMockData,
  RESPONSE_STATUS,
  RESPONSE_SCHEMAS
} from './utils/ResponseUtils';
import {
  RequestManager,
  SlidingWindowRateLimiter,
  PriorityRequestQueue,
  RequestDeduplicator,
  RequestCancellationManager,
  RequestPerformanceMonitor,
  requestManager,
  requestUtils,
  executeRequest,
  cancelRequest,
  cancelAllRequests,
  getRequestStats,
  REQUEST_PRIORITY,
  REQUEST_STATUS,
  RATE_LIMIT_CONFIG,
  REQUEST_CONFIG
} from './utils/RequestManager';

// Core API Client
export {
  apiClient as default,
  createApiClient,
  createSecureApiClient,
  API_CONFIG,
  DEFAULT_HEADERS,
  performanceMonitor
} from './config/ApiClient';

// Re-export existing API configuration for backward compatibility
export {
  HTTP_STATUS,
  apiHelpers,
  createApiService
} from '../../config/api';

// Export interceptors
export {
  createRequestInterceptor,
  createRequestErrorHandler,
  createResponseInterceptor,
  createResponseErrorHandler,
  TokenManager,
  TokenRefreshManager,
  ResponsePerformanceTracker,
  interceptorUtils,
  INTERCEPTORS_MODULE_INFO
} from './interceptors';

// Export endpoint management
export {
  API_ENDPOINTS,
  URLBuilder,
  ParameterValidator,
  EndpointTester,
  urlBuilder,
  endpointUtils,
  API_VERSIONS
};

// Export error handling (already imported above)
export {
  GlobalErrorHandler,
  RetryHandler,
  globalErrorHandler,
  retryHandler,
  errorUtils,
  handleApiError,
  handleNetworkError,
  handleAuthError,
  handleSubscriptionError,
  ERROR_CATEGORIES,
  ERROR_SEVERITY,
  RECOVERY_ACTIONS
};

// Export response utilities (already imported above)
export {
  ResponseProcessor,
  ResponseCache,
  ResponsePerformanceMonitor,
  ResponseValidator,
  ResponseTransformer,
  PaginationHandler,
  MockResponseGenerator,
  responseProcessor,
  responseUtils,
  processApiResponse,
  getCachedResponse,
  validateResponse,
  validateMarketsAnalysis,
  transformMarketsAnalysis,
  generateMockData,
  RESPONSE_STATUS,
  RESPONSE_SCHEMAS
};

// Export request management (already imported above)
export {
  RequestManager,
  SlidingWindowRateLimiter,
  PriorityRequestQueue,
  RequestDeduplicator,
  RequestCancellationManager,
  RequestPerformanceMonitor,
  requestManager,
  requestUtils,
  executeRequest,
  cancelRequest,
  cancelAllRequests,
  getRequestStats,
  REQUEST_PRIORITY,
  REQUEST_STATUS,
  RATE_LIMIT_CONFIG,
  REQUEST_CONFIG
};

/**
 * API Module Information
 */
export const API_MODULE_INFO = {
  name: 'ArbiWin API Integration',
  version: '1.0.0',
  description: 'Centralized API client with interceptors, error handling, and performance monitoring',
  features: [
    'Automatic retry logic',
    'Request/response logging',
    'Performance monitoring',
    'Error boundaries',
    'CORS handling',
    'JWT token management',
    'Token refresh automation',
    'Rate limiting protection',
    'Request queuing',
    'Modular interceptors',
    'Subscription error handling',
    'Response transformation',
    'Security headers injection',
    'Global error handling',
    'Bilingual error messages',
    'Error categorization and severity',
    'Recovery action suggestions',
    'Error statistics and reporting',
    'Network failure detection',
    'Authentication error handling',
    'Exponential backoff retry',
    'Response validation and transformation',
    'Intelligent response caching',
    'Performance monitoring and metrics',
    'Pagination handling utilities',
    'Mock data generation',
    'MarketsAnalysisDTO processing',
    'Bilingual data transformation',
    'Advanced request management',
    'Rate limiting and abuse prevention',
    'Request deduplication',
    'Priority-based request queuing',
    'Request cancellation utilities',
    'Concurrent request limiting'
  ],
  interceptors: INTERCEPTORS_MODULE_INFO,
  errorHandling: {
    categories: Object.keys(ERROR_CATEGORIES),
    severityLevels: Object.keys(ERROR_SEVERITY),
    recoveryActions: Object.keys(RECOVERY_ACTIONS),
    languages: ['bg', 'en'],
    features: [
      'HTTP status code mapping',
      'Network error detection',
      'User-friendly messages',
      'Error logging and reporting',
      'Recovery suggestions',
      'Statistics tracking'
    ]
  },
  responseProcessing: {
    schemas: Object.keys(RESPONSE_SCHEMAS),
    status: Object.keys(RESPONSE_STATUS),
    features: [
      'Schema validation',
      'Data transformation',
      'Pagination handling',
      'Performance monitoring',
      'Response caching',
      'Mock data generation',
      'MarketsAnalysisDTO validation',
      'Bilingual transformations'
    ],
    transformers: [
      'marketsAnalysis',
      'userData',
      'pagination'
    ],
    mockDataTypes: [
      'marketsAnalysis',
      'auth',
      'subscription'
    ]
  },
  requestManagement: {
    priorities: Object.keys(REQUEST_PRIORITY),
    statuses: Object.keys(REQUEST_STATUS),
    rateLimits: {
      global: RATE_LIMIT_CONFIG.GLOBAL,
      endpoints: Object.keys(RATE_LIMIT_CONFIG.ENDPOINTS),
      userLimits: RATE_LIMIT_CONFIG.USER_LIMITS
    },
    features: [
      'Sliding window rate limiting',
      'Priority-based request queuing',
      'Request deduplication',
      'Automatic request cancellation',
      'Concurrent request limiting',
      'Exponential backoff retry',
      'Performance monitoring',
      'Memory-efficient cleanup'
    ],
    configuration: {
      maxConcurrent: REQUEST_CONFIG.MAX_CONCURRENT,
      maxQueueSize: REQUEST_CONFIG.MAX_QUEUE_SIZE,
      requestTimeout: REQUEST_CONFIG.REQUEST_TIMEOUT,
      deduplicationWindow: REQUEST_CONFIG.DEDUPLICATION_WINDOW,
      maxRetries: REQUEST_CONFIG.MAX_RETRIES
    }
  }
};

/**
 * Quick API client access for common operations using new endpoint system
 * Note: These functions are created dynamically to avoid circular imports
 */
export const createApiHelpers = () => {
  const client = apiClient;
  const builder = urlBuilder;

  return {
    // Authentication endpoints
    auth: {
      login: (credentials) => {
        const endpoint = API_ENDPOINTS.AUTH.LOGIN;
        const validation = endpointUtils.validate(endpoint, credentials);
        if (!validation.isValid) {
          return Promise.reject(new Error(`Validation failed: ${validation.errors.join(', ')}`));
        }
        return client.post(endpoint.path, credentials);
      },
      register: (userData) => {
        const endpoint = API_ENDPOINTS.AUTH.REGISTER;
        const validation = endpointUtils.validate(endpoint, userData);
        if (!validation.isValid) {
          return Promise.reject(new Error(`Validation failed: ${validation.errors.join(', ')}`));
        }
        return client.post(endpoint.path, userData);
      },
      logout: () => client.post(API_ENDPOINTS.AUTH.LOGOUT.path),
      refreshToken: (refreshToken) => client.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN.path, { refreshToken }),
      forgotPassword: (email) => client.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD.path, { email }),
      resetPassword: (token, password, confirmPassword) =>
        client.post(API_ENDPOINTS.AUTH.RESET_PASSWORD.path, { token, password, confirmPassword })
    },

    // Subscription endpoints
    subscription: {
      getStatus: () => client.get(API_ENDPOINTS.SUBSCRIPTION.GET_STATUS.path),
      activate: (planId) => client.post(API_ENDPOINTS.SUBSCRIPTION.ACTIVATE.path, planId ? { planId } : {}),
      deactivate: (reason) => client.post(API_ENDPOINTS.SUBSCRIPTION.DEACTIVATE.path, reason ? { reason } : {}),
      getHistory: (pagination = {}) => {
        const url = builder.buildURL(API_ENDPOINTS.SUBSCRIPTION.GET_HISTORY, {}, pagination);
        return client.get(url);
      },
      getPlans: (currency) => {
        const queryParams = currency ? { currency } : {};
        const url = builder.buildURL(API_ENDPOINTS.SUBSCRIPTION.GET_PLANS, {}, queryParams);
        return client.get(url);
      }
    },

    // Surebet endpoints
    surebet: {
      getMarketsAnalysis: (filters = {}) => {
        const validation = endpointUtils.validateFilters(filters);
        if (!validation.isValid) {
          return Promise.reject(new Error(`Filter validation failed: ${validation.errors.join(', ')}`));
        }
        const url = builder.buildSurebetURL(filters);
        return client.get(url);
      },
      getBookmakers: (params = {}) => {
        const url = builder.buildURL(API_ENDPOINTS.SUREBET.GET_BOOKMAKERS, {}, params);
        return client.get(url);
      },
      getSports: (params = {}) => {
        const url = builder.buildURL(API_ENDPOINTS.SUREBET.GET_SPORTS, {}, params);
        return client.get(url);
      },
      getChampionships: (params = {}) => {
        const url = builder.buildURL(API_ENDPOINTS.SUREBET.GET_CHAMPIONSHIPS, {}, params);
        return client.get(url);
      }
    },

    // User endpoints
    user: {
      getProfile: () => client.get(API_ENDPOINTS.USER.GET_PROFILE.path),
      updateProfile: (profileData) => client.put(API_ENDPOINTS.USER.UPDATE_PROFILE.path, profileData),
      getPreferences: () => client.get(API_ENDPOINTS.USER.GET_PREFERENCES.path),
      updatePreferences: (preferences) => client.put(API_ENDPOINTS.USER.UPDATE_PREFERENCES.path, preferences),
      deleteAccount: (password, reason) =>
        client.delete(API_ENDPOINTS.USER.DELETE_ACCOUNT.path, { data: { password, reason } }),
      changePassword: (passwords) => client.post(API_ENDPOINTS.USER.CHANGE_PASSWORD.path, passwords)
    },

    // Utility endpoints
    health: () => client.get(API_ENDPOINTS.UTILITY.HEALTH_CHECK.path),
    version: () => client.get(API_ENDPOINTS.UTILITY.VERSION_INFO.path)
  };
};

// Export the API helpers as a lazy-loaded object
export const api = createApiHelpers();

// apiClient is already imported above
